/** The relations row's drawers (the tour plan §2 "Relations row", D11): a link-button with the
 *  relation's count that opens a right-side drawer, and the paging and failure parts the
 *  drawers share. The count comes from the first page of the drawer's own list, so opening
 *  it shows what the count counted. */
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Drawer, IconButton, Link, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export const RELATION_PAGE_SIZE = 25;

/** "Chat channels · 12" (the count once known), a button. */
export function RelationLink({
  label,
  count,
  failed,
  onOpen,
}: {
  label: string;
  /** null = not known yet (loading, or the server could not count). */
  count: number | null;
  failed: boolean;
  onOpen: () => void;
}) {
  const suffix = failed ? ' · couldn’t load' : count !== null && count >= 0 ? ` · ${count}` : '';
  return (
    <Link component="button" type="button" variant="body2" underline="hover" onClick={onOpen} sx={{ verticalAlign: 'baseline' }}>
      {label}
      {suffix}
    </Link>
  );
}

export function RelationDrawer({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 560, maxWidth: '100vw' }, role: 'dialog', 'aria-label': title } as object }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, flex: 1 }}>
          {title}
        </Typography>
        <IconButton size="small" aria-label="Close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: 2, display: 'grid', gap: 1.5, alignContent: 'start', overflowY: 'auto' }}>{children}</Box>
    </Drawer>
  );
}

export function RelationFailure({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <Typography variant="body2" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }} data-relation-error>
      <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
      Couldn’t load {what} —
      <Button size="small" color="inherit" onClick={onRetry} sx={{ minWidth: 0, py: 0 }}>
        Retry
      </Button>
    </Typography>
  );
}

/** Previous / Next with "Page 2 of 4" (count known) or "Page 2". */
export function RelationPager({
  page,
  count,
  shown,
  onPage,
}: {
  page: number;
  count: number;
  shown: number;
  onPage: (page: number) => void;
}) {
  const pages = count >= 0 ? Math.max(1, Math.ceil(count / RELATION_PAGE_SIZE)) : null;
  const hasNext = pages !== null ? page < pages : shown === RELATION_PAGE_SIZE;
  if (page === 1 && !hasNext) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
      <Button size="small" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <Typography variant="caption" color="text.secondary">
        Page {page}
        {pages !== null ? ` of ${pages}` : ''}
      </Typography>
      <Button size="small" disabled={!hasNext} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </Box>
  );
}
